// One run on Claims → Runs, beside the list or full width: its actions, its
// totals, and four tabs — Lines, Self-audit, Filing, Remittances.
//
// Replaced RunDetail.js (2026-10-20), which stacked everything inside the
// run's table row. Nothing here writes to the database: every action goes
// back to the page through a callback.

import { useEffect, useRef, useState } from 'react';
import { identityProblems } from '../../../lib/edi/billingIdentity';
import { explainEobs } from '../../../lib/edi/eobCodes';
import { destinationLabel, filesWithGammis } from '../../../lib/serviceModel';
import RunAuditPanel from './RunAuditPanel';
import { SubmissionPanel, TransportPanel, ExportProblems } from './FilingPanels';
import { StatusAnomalies, PendingRemittances, SortArrow } from './RunPanels';
import { STATUS_COLOR, LINE_STATUS_COLOR, fmt$2, fmtDate } from './format';
import { weekLabel } from './RunList';

export const RUN_TABS = ['lines', 'audit', 'filing', 'remittances'];

const sortLines = (lines, sortKey, sortAsc) => [...lines].sort((a, b) => {
  const av = a[sortKey]; const bv = b[sortKey];
  if (av == null && bv == null) return 0;
  if (av == null) return 1; if (bv == null) return -1;
  const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
  return sortAsc ? cmp : -cmp;
});

// A line billed on scheduled hours because nobody clocked it.
const unclocked = (l) => l.hours_basis === 'clocked_else_scheduled' && !Number(l.clocked_hours);

function MoreMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc, true); };
  }, [open]);
  const shown = items.filter(Boolean);
  if (!shown.length) return null;
  return (
    <div className="run-more" ref={ref}>
      <button className="btn btn-outline btn-sm" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>More ▾</button>
      {open && (
        <div className="run-more-menu" role="menu">
          {shown.map(it => it.divider
            ? <div key={it.key} className="run-more-divider" role="separator" />
            : (
              <button key={it.label} role="menuitem" className={it.danger ? 'danger' : ''} disabled={it.disabled}
                onClick={() => { setOpen(false); it.onClick(); }}>{it.label}</button>
            ))}
        </div>
      )}
    </div>
  );
}

export default function RunPanel({
  run, lines, linesLoading, mark, tab, onTab, full, onFull, onShowList, isMobile, onBack,
  onCsv, on837P, submitting, onResync, onEnterRef, onPostRemit, onDelete, onRecheck,
  exportProblems, onDismissProblems,
  identity, settings, transportConfigured, transport, transfers, anySubmissions, onClaimStatus, onRefresh,
  subs, onDownloadFile, onSendFile, onMarkSent, onAcknowledgement,
  pendingRemits, onOpenStoredRemittance, eobIndex,
  sortKey, sortAsc, onSort, onOpenLine,
}) {
  const [lineFilter, setLineFilter] = useState('all');
  const [lineSearch, setLineSearch] = useState('');
  useEffect(() => { setLineFilter('all'); setLineSearch(''); }, [run.id]);

  const sc = STATUS_COLOR[run.status] || STATUS_COLOR.draft;
  const gammis = filesWithGammis(run);
  const approved = run.status === 'approved';
  const changedIds = new Set(mark?.state === 'done' ? mark.changedLineIds || [] : []);
  const idProblems = identity ? identityProblems(identity, { forTransmission: true }) : [];

  const q = lineSearch.trim().toLowerCase();
  const filtered = lines.filter(l =>
    (lineFilter === 'all' || (lineFilter === 'unclocked' && unclocked(l)) || (lineFilter === 'changed' && changedIds.has(l.id)))
    && (!q || [l.client_name, l.medicaid_id, l.icn].some(v => String(v || '').toLowerCase().includes(q))));
  const shownLines = sortLines(filtered, sortKey, sortAsc);
  const anyPaid = lines.some(l => l.paid_amount != null);

  const auditLabel = !mark ? 'not checked yet'
    : mark.state === 'checking' ? 'checking…'
    : mark.state === 'error' ? 'could not check'
    : [mark.serious ? `${mark.serious} serious` : null, mark.review ? `${mark.review} to review` : null].filter(Boolean).join(' · ') || 'nothing found';

  const tabLabel = {
    lines: `Lines (${lines.length})`,
    audit: `Self-audit${mark?.state === 'done' && mark.serious ? ` · ${mark.serious} serious` : ''}`,
    filing: 'Filing',
    remittances: 'Remittances',
  };

  const th = (key, label, num) => (
    <th key={key} onClick={() => onSort(key)} className={num ? 'num' : ''}
      style={{ cursor: 'pointer', userSelect: 'none', color: sortKey === key ? 'var(--accent)' : undefined }}>
      {label}<SortArrow field={key} sortKey={sortKey} sortAsc={sortAsc} />
    </th>
  );

  return (
    <section className="run-panel" aria-label={`Run ${weekLabel(run)}`}>
      <div className="run-panel-head">
        <div className="run-panel-title">
          {isMobile && <button className="btn btn-ghost btn-sm" onClick={onBack}>‹ Runs</button>}
          <h2>{fmtDate(run.date_from)} – {fmtDate(run.date_to)}</h2>
          <span className="run-chip" style={{ background: gammis ? 'var(--bg2)' : 'var(--accent-bg)', color: gammis ? 'var(--text2)' : 'var(--accent)' }}
            title={gammis ? 'Files with GAMMIS over SFTP' : 'Filed with CareSource outside CAERA'}>{destinationLabel(run.payer)}</span>
          <span className="run-chip" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
        </div>
        <div className="run-panel-actions">
          {full && !isMobile && <button className="btn btn-outline btn-sm" onClick={onShowList}>‹ Show runs list</button>}
          <button className="btn btn-outline btn-sm" onClick={onCsv} title="Export GAMMIS CSV">↓ CSV</button>
          {approved && <button className="btn btn-outline btn-sm" onClick={onResync} title="Resync with AxisCare">⟳ Resync</button>}
          <MoreMenu items={[
            gammis && { label: submitting ? 'Checking…' : '↓ 837P for GAMMIS', onClick: on837P, disabled: submitting },
            approved && { label: gammis ? 'Enter ICN' : 'Record claim no.', onClick: onEnterRef },
            run.status === 'submitted' && !run.gammis_batch_ref && { label: gammis ? 'Add ICN' : 'Add claim no.', onClick: onEnterRef },
            run.status !== 'draft' && { label: '↑ Post a remittance (RA / ERA)', onClick: onPostRemit },
            run.ra_pdf_url && { label: '📄 Open the RA PDF', onClick: () => window.open(run.ra_pdf_url, '_blank', 'noopener') },
            { label: 'Check marks again', onClick: onRecheck },
            approved && { divider: true, key: 'd' },
            approved && { label: 'Delete run…', onClick: onDelete, danger: true },
          ]} />
          {!full && !isMobile && <button className="btn btn-outline btn-sm" onClick={onFull} title="Show this run full width (F)">⤢ Full width</button>}
        </div>
      </div>

      <ExportProblems problems={exportProblems?.runId === run.id ? exportProblems : null} onDismiss={onDismissProblems} />

      <div className="run-kpis">
        <div className="run-kpi"><span>Lines</span><b>{run.total_lines ?? lines.length}</b><small>{lines.filter(unclocked).length} on scheduled hours</small></div>
        <div className="run-kpi"><span>Accrued</span><b>{fmt$2(run.total_accrued_amount)}</b><small>Medicaid-payable</small></div>
        <div className="run-kpi"><span>Charge</span><b>{fmt$2(run.total_charge_amount)}</b><small>{run.charge_multiplier ? `${Number(run.charge_multiplier)}× accrued` : ' '}</small></div>
        <div className="run-kpi"><span>Paid</span><b style={{ color: run.total_paid_amount ? 'var(--success)' : undefined }}>{fmt$2(run.total_paid_amount || 0)}</b><small>{run.ra_reference ? `RA ${run.ra_reference}` : 'no remittance yet'}</small></div>
        <div className="run-kpi"><span>Self-audit</span><b style={{ color: mark?.serious ? 'var(--danger)' : undefined, fontSize: 15 }}>{auditLabel}</b>
          <small>{mark?.state === 'done' && mark.changed != null ? `${mark.changed} changed · ${mark.newVisits} new in AxisCare` : ' '}</small></div>
      </div>

      <div className="run-tabs" role="tablist" aria-label="Run sections">
        {RUN_TABS.map(t => (
          <button key={t} role="tab" aria-selected={tab === t} className={tab === t ? 'on' : ''} onClick={() => onTab(t)}>{tabLabel[t]}</button>
        ))}
      </div>

      {tab === 'lines' && (
        <div className="run-tab-body">
          <div className="run-lines-tools">
            <div className="runs-filters" role="group" aria-label="Show lines">
              {[['all', `All ${lines.length}`], ['unclocked', `No clock-in ${lines.filter(unclocked).length}`], ['changed', `Changed in AxisCare ${changedIds.size}`]].map(([k, label]) => (
                <button key={k} className={`runs-filter${lineFilter === k ? ' on' : ''}`} aria-pressed={lineFilter === k} onClick={() => setLineFilter(k)}>{label}</button>
              ))}
            </div>
            <label className="sr-only" htmlFor="line-search">Find a line</label>
            <input id="line-search" type="search" className="form-input" placeholder="Client, Medicaid ID or ICN"
              value={lineSearch} onChange={e => setLineSearch(e.target.value)} style={{ maxWidth: 260 }} />
          </div>
          {linesLoading ? (
            <div style={{ padding: 16, textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
          ) : (
            <div className="run-table-box">
              <table className="run-table">
                <thead><tr>
                  {th('client_name', 'Client')}{th('service_date', 'Date')}{th('service_code', 'Code')}{th('hours', 'Hours', true)}
                  {th('accrued_amount', 'Accrued', true)}{th('charge_amount', 'Charge', true)}
                  {anyPaid && th('paid_amount', 'Paid', true)}
                  {th('status', 'Status')}
                  <th><span className="sr-only">Line actions</span></th>
                </tr></thead>
                <tbody>
                  {shownLines.map(l => {
                    const lc = LINE_STATUS_COLOR[l.status] || LINE_STATUS_COLOR.pending;
                    const flags = [unclocked(l) ? 'no clock-in' : null, changedIds.has(l.id) ? 'changed in AxisCare' : null].filter(Boolean);
                    return (
                      <tr key={l.id} onClick={() => onOpenLine(l)} className="run-line">
                        <td><b>{l.client_name || '—'}</b>{flags.length > 0 && <div className="run-line-flags">{flags.join(' · ')}</div>}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{l.service_date || '—'}</td>
                        <td className="mono">{l.service_code || '—'}{l.modifier ? ` ${l.modifier}` : ''}</td>
                        <td className="num">{l.hours ? `${l.hours}h` : '—'}</td>
                        <td className="num">{l.accrued_amount != null ? fmt$2(l.accrued_amount) : '—'}</td>
                        <td className="num">{l.charge_amount ? fmt$2(l.charge_amount) : '—'}</td>
                        {anyPaid && <td className="num" style={{ color: l.paid_amount ? 'var(--success)' : 'var(--text3)' }}>{l.paid_amount != null ? fmt$2(l.paid_amount) : '—'}</td>}
                        <td><span className="run-chip" style={{ background: lc.bg, color: lc.color }}>{l.status}</span></td>
                        <td className="act"><button className="btn btn-ghost btn-sm" aria-label={`Open ${l.client_name || 'line'} ${l.service_date || ''}`}
                          onClick={e => { e.stopPropagation(); onOpenLine(l); }}>⋯</button></td>
                      </tr>
                    );
                  })}
                  {shownLines.length === 0 && <tr><td colSpan={9} style={{ color: 'var(--text3)', padding: 16 }}>No line matches.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'audit' && (
        <div className="run-tab-body">
          {linesLoading ? <div style={{ padding: 16, textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
            : lines.length ? <RunAuditPanel run={run} lines={lines} /> : <p className="runs-list-note">This run has no lines to check.</p>}
        </div>
      )}

      {tab === 'filing' && (
        <div className="run-tab-body">
          {gammis && idProblems.length > 0 && (
            <div className="run-note warn">
              <b>Billing identity incomplete — 837P export is unavailable</b>
              <ul>{idProblems.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </div>
          )}
          <div className="run-filing-actions">
            <button className="btn btn-outline btn-sm" onClick={onCsv}>↓ CSV</button>
            {gammis && <button className="btn btn-outline btn-sm" onClick={on837P} disabled={submitting}>{submitting ? 'Checking…' : '↓ 837P for GAMMIS'}</button>}
            {(approved || (run.status === 'submitted' && !run.gammis_batch_ref)) && (
              <button className="btn btn-outline btn-sm" onClick={onEnterRef}>{gammis ? 'Enter ICN' : 'Record claim no.'}</button>
            )}
            {run.gammis_batch_ref && <span className="runs-list-note">Claim ref / ICN <b className="mono">{run.gammis_batch_ref}</b></span>}
          </div>
          {!gammis && <p className="runs-list-note">A {destinationLabel(run.payer)} run is filed with {destinationLabel(run.payer)} outside CAERA: export the CSV, file it there, and record the claim number here.</p>}
          <SubmissionPanel subs={subs} transportConfigured={transportConfigured} transportBusy={transport?.busy}
            onDownload={onDownloadFile} onSend={onSendFile} onMarkSent={onMarkSent} onAcknowledgement={onAcknowledgement} />
          {gammis && !subs.length && <p className="runs-list-note">No 837P has been generated for this run.</p>}
          <StatusAnomalies lines={lines} />
          {gammis && (
            <TransportPanel settings={settings} transportConfigured={transportConfigured} transport={transport}
              transfers={transfers} anySubmissions={anySubmissions} onClaimStatus={onClaimStatus} onRefresh={onRefresh} />
          )}
        </div>
      )}

      {tab === 'remittances' && (
        <div className="run-tab-body">
          <div className="run-filing-actions">
            {run.status !== 'draft' && <button className="btn btn-outline btn-sm" onClick={onPostRemit}>↑ Post a remittance (RA / ERA)</button>}
            {run.ra_reference && (
              <span className="runs-list-note">Remittance advice <b className="mono">{run.ra_reference}</b>
                {run.ra_pdf_url && <> · <a href={run.ra_pdf_url} target="_blank" rel="noopener noreferrer">View PDF ↗</a></>}</span>
            )}
          </div>
          <PendingRemittances run={run} pendingRemits={pendingRemits} onOpen={onOpenStoredRemittance} />
          <div className="run-table-box">
            <table className="run-table">
              <thead><tr>
                {th('client_name', 'Client')}{th('service_date', 'Date')}{th('charge_amount', 'Charge', true)}
                {th('allowed_amount', 'Allowed', true)}{th('paid_amount', 'Paid', true)}{th('icn', 'ICN')}<th>EOBS</th>{th('status', 'Status')}
              </tr></thead>
              <tbody>
                {sortLines(lines, sortKey, sortAsc).map(l => {
                  const lc = LINE_STATUS_COLOR[l.status] || LINE_STATUS_COLOR.pending;
                  const eobs = l.eobs_codes ? explainEobs(l.eobs_codes, eobIndex) : [];
                  return (
                    <tr key={l.id} onClick={() => onOpenLine(l)} className="run-line">
                      <td><b>{l.client_name || '—'}</b></td>
                      <td style={{ whiteSpace: 'nowrap' }}>{l.service_date || '—'}</td>
                      <td className="num">{l.charge_amount ? fmt$2(l.charge_amount) : '—'}</td>
                      <td className="num">{l.allowed_amount != null ? fmt$2(l.allowed_amount) : '—'}</td>
                      <td className="num" style={{ color: l.paid_amount ? 'var(--success)' : 'var(--text3)' }}>{l.paid_amount != null ? fmt$2(l.paid_amount) : '—'}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{l.icn || '—'}</td>
                      <td style={{ fontSize: 11, maxWidth: 220 }}>
                        {l.eobs_codes ? (<>
                          <span style={{ color: 'var(--danger)' }} title={eobs.map(e => e.text).join('\n')}>{l.eobs_codes}</span>
                          {eobs.filter(e => e.description).map((e, i) => <div key={i} style={{ color: 'var(--text3)' }}>{e.description}</div>)}
                        </>) : '—'}
                      </td>
                      <td><span className="run-chip" style={{ background: lc.bg, color: lc.color }}>{l.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
